// public/audio/manifest.json, written by tools/music/build.mjs: which cue is which file, how long
// it is and how it loops.

import { BED_CUES, STING_CUES, type CueId, type Source } from './types.ts';

export const MANIFEST_VERSION = 1;

/** Folder of the audio files under the site's base URL (public/audio). */
export const AUDIO_DIR = 'audio';

export interface FileEntry {
  /** Path under public/audio. */
  file: string;
  seconds: number;
  lufs: number;
  tp: number;
  bytes: number;
  /** Beds: seconds the engine blends the end of the file into its start. */
  crossfade?: number;
}

export interface CueEntry {
  id: CueId;
  kind: 'bed' | 'sting';
  loop: boolean;
  /** Level trim the engine applies on top of the mastered file. */
  gainDb: number;
  use: string;
  files: Partial<Record<Source, FileEntry>> & { orchestra: FileEntry };
}

export interface Manifest {
  version: typeof MANIFEST_VERSION;
  cues: CueEntry[];
}

const KNOWN_CUES: readonly string[] = [...BED_CUES, ...STING_CUES];

function record(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`audio manifest: ${what} is not an object`);
  }
  return value as Record<string, unknown>;
}

function number(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`audio manifest: ${what} is not a number`);
  }
  return value;
}

function fileEntry(value: unknown, what: string): FileEntry {
  const entry = record(value, what);
  if (typeof entry.file !== 'string') {
    throw new Error(`audio manifest: ${what}.file is not a string`);
  }

  const out: FileEntry = {
    file: entry.file,
    seconds: number(entry.seconds, `${what}.seconds`),
    lufs: number(entry.lufs, `${what}.lufs`),
    tp: number(entry.tp, `${what}.tp`),
    bytes: number(entry.bytes, `${what}.bytes`),
  };
  if (entry.crossfade !== undefined) {
    out.crossfade = number(entry.crossfade, `${what}.crossfade`);
  }
  return out;
}

function cueEntry(value: unknown, index: number): CueEntry {
  const what = `cues[${index}]`;
  const cue = record(value, what);
  if (typeof cue.id !== 'string' || !KNOWN_CUES.includes(cue.id)) {
    throw new Error(`audio manifest: ${what}.id "${String(cue.id)}" is not a known cue`);
  }

  const files = record(cue.files, `${what}.files`);
  const parsed: CueEntry['files'] = { orchestra: fileEntry(files.orchestra, `${what}.files.orchestra`) };
  if (files.cc0 !== undefined) {
    parsed.cc0 = fileEntry(files.cc0, `${what}.files.cc0`);
  }

  return {
    id: cue.id as CueId,
    kind: (BED_CUES as readonly string[]).includes(cue.id) ? 'bed' : 'sting',
    loop: cue.loop === true,
    gainDb: number(cue.gainDb, `${what}.gainDb`),
    use: typeof cue.use === 'string' ? cue.use : '',
    files: parsed,
  };
}

/** Check and type the manifest JSON; throws with the first thing that is wrong. */
export function parseManifest(json: unknown): Manifest {
  const root = record(json, 'root');
  if (root.version !== MANIFEST_VERSION) {
    throw new Error(`audio manifest: version ${String(root.version)}, expected ${MANIFEST_VERSION}`);
  }
  if (!Array.isArray(root.cues)) {
    throw new Error('audio manifest: cues is not a list');
  }
  return { version: MANIFEST_VERSION, cues: root.cues.map(cueEntry) };
}

export function cueOf(manifest: Manifest, id: CueId): CueEntry | null {
  return manifest.cues.find((c) => c.id === id) ?? null;
}

/** The file for a cue in the chosen source; cues with no CC0 version play their orchestra file. */
export function fileFor(manifest: Manifest, id: CueId, source: Source): FileEntry | null {
  const cue = cueOf(manifest, id);
  if (cue === null) {
    return null;
  }
  return cue.files[source] ?? cue.files.orchestra;
}

export function urlFor(baseUrl: string, file: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${base}${AUDIO_DIR}/${file}`;
}
